/* eslint-disable react/display-name */
import { memo } from 'react';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { Button, PageTitle } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Check, HookFormResolver, Text, TextArea } from '@dec/shared';
import { type HookFormMethods } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { Col } from '@dec/shared';
import { Row } from '@dec/shared';
import { ConfirmDialog } from 'app/core/dialog';
import { notificationSuccess as notifySuccess } from 'app/core/toast';
import { useSpinnerActions } from 'app/layouts/store';
import { Col as ColReactBootsrap } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { label as labelCommon, messages as messagesCommon } from '../../../../translation';
import { createFormComments } from '../../createFormComments';
import { useGetTaxReturnDef } from '../../hooks';
import service from '../../services/taxReturnEvaluation.service';
import { buttons, label, messages, title } from '../../translation';
import { type RejectTaxReturnDto } from '../../types';

import validations from './validations-reject';

interface Props {}

export const RejectTaxreturn = memo((props: Props) => {
  const { id } = useParams<Record<string, string>>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { loading, data, error } = useGetTaxReturnDef(id ?? '');
  const { definition: formDef, taxreturn } = data;
  const { showSpinner, hideSpinner } = useSpinnerActions();

  if (loading || error) {
    return <></>;
  }

  const onSubmit = async (values: RejectTaxReturnDto) => {
    const ok = await ConfirmDialog({
      titleModal: title(t, 'dialog-confirm'),
      message: messages(t, 'confirm'),
      okButton: labelCommon('ok'),
      cancelButton: labelCommon('cancel')
    });
    if (!ok) {
      return;
    }

    values.id = id ?? '';
    showSpinner();
    try {
      const { error: submitError } = (await service.reject(values)) as any;
      if (submitError) {
        return;
      }
      notifySuccess(t('taxreturn-evaluation.messages.rejected'));
      navigate('/statements/taxreturn-evaluation');
    } finally {
      hideSpinner();
    }
  };

  return (
    <LoadingOverlay active={loading} spinner text={messagesCommon('pleaseWait')}>
      <PageTitle title={title(t, 'reject')} />
      <HookFormResolver
        onSubmit={onSubmit}
        initialValues={createFormComments(formDef as any)}
        validations={validations}
      >
        {({ formState: { errors }, reset, setValue, control }: HookFormMethods) => {
          return (
            <>
              <Row>
                <Col>
                  <Text
                    name="formName"
                    defaultValue={`${taxreturn?.formVersion?.form?.code} - ${taxreturn?.formVersion?.form?.shortName}`}
                    label={label(t, 'form')}
                    readonly={true}
                  />
                </Col>
                <Col>
                  <Text
                    name="perid"
                    defaultValue={taxreturn?.period?.toString()}
                    label={label(t, 'taxpayer')}
                    readonly={true}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text
                    name="taxpayerName"
                    defaultValue={taxreturn?.taxpayer?.name}
                    label={label(t, 'taxpayer')}
                    readonly={true}
                  />
                </Col>
              </Row>
              <hr></hr>
              {formDef?.sections ? (
                formDef.sections.map((x, i) => {
                  return (
                    <Row key={i}>
                      <Col>
                        <Row>
                          <Col>
                            <h2>{x.name}</h2>
                          </Col>
                        </Row>
                        <Row>
                          <ColReactBootsrap sm={10}>
                            <TextArea
                              rows={3}
                              label={label(t, 'comments')}
                              name={`sections[${i}].comment`}
                            />
                          </ColReactBootsrap>
                          <ColReactBootsrap>
                            <Check label={label(t, 'approve')} name={`sections[${i}].approved`} />
                          </ColReactBootsrap>
                          <Col>
                            <Text
                              hidden
                              defaultValue={x.name}
                              label=""
                              name={`sections[${i}].sectionName`}
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  );
                })
              ) : (
                <></>
              )}
              <hr></hr>

              <Row>
                <Col>
                  <TextArea rows={4} label={label(t, 'general-comments')} name="comments" />
                </Col>
              </Row>

              <BtnToolbar>
                <BtnGroup>
                  <>
                    <Button
                      action={ButtonAction.cancel}
                      variant="secondary"
                      onClick={() => navigate(`/statements/taxreturn-evaluation/${id}`)}
                    />
                  </>
                </BtnGroup>
                <BtnGroup>
                  <Button
                    action={ButtonAction.delete}
                    variant="danger"
                    text={buttons(t, 'reject')}
                    type="submit"
                  />
                </BtnGroup>
              </BtnToolbar>
            </>
          );
        }}
      </HookFormResolver>
    </LoadingOverlay>
  );
});
